/* eslint-disable no-param-reassign */
import { Injectable, inject } from '@angular/core';

import { FixtureOfferItems, FixturePage } from '@cds/betting-offer';
import { CountsFixturesResponse, FixtureView, PicksView } from '@cds/betting-offer/domain-specific';
import { LiveHighlightsResponse } from '@cds/betting-offer/domain-specific/live';
import { VirtualFixtureView, VirtualSportView } from '@cds/betting-offer/domain-specific/virtual';
import { TagCount, TagType } from '@cds/betting-offer/tags';
import {
    BatchRequest,
    CountsFixturesRequest,
    FixtureCountsRequest,
    FixtureRequest,
    FixtureState,
    FixtureViewRequest,
    LiveHighlightsRequest,
    LiveStreamingRequest,
    OfferMapping,
    ParticipantMapping,
    PicksRequest,
    ScoreboardMode,
    SortByCriteria,
    Tv1PickData,
    Tv2PickData,
    VirtualOfferRequest,
} from '@cds/query-objects';
import { isV2CompoundId } from '@frontend/sports/betting-offer/feature/model';
import {
    BaseTranslationConfig,
    BettingOfferConfig,
    CdsConfig,
    MarketGroupingConfig,
    SportsConfig,
} from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { ExpiringCache } from '@frontend/sports/common/core/utils/expiring-cache';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { BaseCdsApi, BaseCdsApiFactory, CDS_API_FACTORY, CdsRequestOptions } from '@frontend/sports/content-distribution/feature';
import { Page } from '@frontend/vanilla/core';
import { chunk, concat, merge } from 'lodash-es';
import { Observable, catchError, firstValueFrom, forkJoin, map } from 'rxjs';

import { FixtureViewModelOptions } from './betting-offer.service';

export interface BatchResponse {
    [key: string]: FixturePage;
}

interface FixtureViewOptions extends FixtureViewModelOptions {
    gridGroupIds?: string;
    useRegionalisedConfiguration?: boolean;
    gameIds?: string;
    optionMarketIds?: string;
}

@Injectable({ providedIn: 'root' })
export class BettingOfferApi {
    private cdsConfig = inject(CdsConfig);
    private sportConfig = inject(SportsConfig);
    private baseTranslationConfig = inject(BaseTranslationConfig);
    private page = inject(Page);
    private marketGroupingConfig = inject(MarketGroupingConfig);
    private bettingOfferConfig = inject(BettingOfferConfig);

    private cdsApi: BaseCdsApi;
    private pickCache: ExpiringCache;
    private readonly logger: SportsRemoteLogger;

    constructor() {
        const cdsApiServiceFactory = inject<BaseCdsApiFactory>(CDS_API_FACTORY);
        const loggerFactory = inject(LoggerFactory);

        this.cdsApi = cdsApiServiceFactory({ endpoint: '/bettingoffer' });
        this.pickCache = new ExpiringCache(0.5);
        this.logger = loggerFactory.getLogger('BettingOfferApi');
    }

    getFixtureView(options: FixtureViewOptions): Promise<FixtureView | undefined> {
        const {
            id,
            statsMode,
            excludeMarkets = false,
            includePrecreated = false,
            isBettingInsightsEnabled = false,
            gridGroupIds,
            useRegionalisedConfiguration = this.marketGroupingConfig.regionalisedGroupingEnabled,
            gameIds,
            optionMarketIds,
            marketGroupIds,
            includeRelatedFixtures = this.bettingOfferConfig.includeLinkedFixtures && !isV2CompoundId(id),
            marketGroupItemIds,
            participantMapping,
            dynamicOfferCategories,
            state,
        } = options;

        const offerMappingFilter =
            gridGroupIds || gameIds || optionMarketIds || marketGroupIds || marketGroupItemIds || dynamicOfferCategories
                ? OfferMapping.Filtered
                : OfferMapping.All;
        const offerMapping = excludeMarkets ? OfferMapping.None : offerMappingFilter;

        return firstValueFrom(
            this.cdsApi.get<FixtureView, FixtureViewRequest>('/fixture-view', {
                offerMapping,
                gridGroupIds,
                gameIds,
                optionMarketIds,
                scoreboardMode: ScoreboardMode.Full,
                fixtureIds: id,
                state: state ?? FixtureState.Latest,
                includePrecreatedBetBuilder: includePrecreated,
                supportVirtual: this.sportConfig.virtuals.isEnabled,
                isBettingInsightsEnabled,
                useRegionalisedConfiguration,
                includeRelatedFixtures,
                statisticsModes: statsMode,
                marketGroupIds,
                marketGroupItemIds,
                participantMapping,
                dynamicOfferCategories,
            }),
        );
    }

    getVirtualFixtureView(sport: number, competition: number, fixture: string): Observable<VirtualFixtureView | undefined> {
        return this.cdsApi.get<VirtualFixtureView, VirtualOfferRequest>('/virtual/fixture-view', {
            offerMapping: OfferMapping.All,
            sportIds: sport.toString(),
            competitionIds: competition.toString(),
            fixtureIds: fixture,
            scheduleSize: this.sportConfig.virtuals.upcomingEventCount,
        });
    }

    /**
     * Caches the request for 500ms so that requests coming with the same parameters will not make multiple CDS calls.
     * This happens because calls are coming from Quickbet/Betslip at the same time
     *
     * @param request
     * @returns
     * @memberof BettingOfferApi
     */
    getPicks(request: PicksRequest, options?: CdsRequestOptions): Observable<FixturePage | undefined> {
        const requestKey = this.buildPicksRequestKey('picks', request);
        if (this.useBaseTranslation()) {
            request.alternateLanguage = this.baseTranslationConfig.defaultLanguage;
        }

        const source = this.cdsApi.post<PicksView>(`/picks`, request, options).pipe(map((response) => response?.fixturePage));

        return this.pickCache.getOrCreate(requestKey, source);
    }

    getReBetPicksView(request: PicksRequest, options?: CdsRequestOptions): Observable<PicksView | undefined> {
        if (this.useBaseTranslation()) {
            request.alternateLanguage = this.baseTranslationConfig.defaultLanguage;
        }

        return this.cdsApi.post<PicksView>(`/picks/re-bet`, request, options);
    }

    /**
     * Caches the request for 500ms so that requests coming with the same parameters will not make multiple CDS calls.
     * This happens because calls are coming from Quickbet/Betslip at the same time
     *
     * @param request
     * @returns
     * @memberof BettingOfferApi
     */
    getPicksViewCache(request: PicksRequest, options?: CdsRequestOptions): Observable<PicksView | undefined> {
        const requestKey = this.buildPicksRequestKey('picks-view', request);
        if (this.useBaseTranslation()) {
            request.alternateLanguage = this.baseTranslationConfig.defaultLanguage;
        }

        const source = this.cdsApi.post<PicksView>(`/picks`, request, options).pipe(map((response) => response));

        return this.pickCache.getOrCreate(requestKey, source);
    }

    getPicksWithFallback(request: PicksRequest): Observable<PicksView | undefined> {
        if (this.useBaseTranslation()) {
            request.alternateLanguage = this.baseTranslationConfig.defaultLanguage;
        }

        return this.cdsApi.post<PicksView>(`/picks-with-fallback`, request);
    }

    getCounts(request: FixtureCountsRequest, options?: CdsRequestOptions): Observable<TagCount[] | undefined> {
        return this.cdsApi.get<TagCount[]>('/counts', request, options);
    }

    getSportList(interval?: { from?: string; to?: string }, sportIds?: number[]): Observable<TagCount[] | undefined> {
        return this.cdsApi.get<TagCount[], FixtureCountsRequest>('/counts', {
            state: FixtureState.Latest,
            tagTypes: TagType.Sport,
            extendedTags: TagType.Sport,
            sortBy: SortByCriteria.Tags,
            supportVirtual: this.sportConfig.virtuals.isEnabled,
            sportIds: sportIds?.join(','),
            ...interval,
        });
    }

    getVirtualSportList(): Observable<VirtualSportView[] | undefined> {
        return this.cdsApi.get<VirtualSportView[], VirtualOfferRequest>('/virtual/sports', {
            scheduleSize: this.sportConfig.virtuals.upcomingEventCount,
        });
    }

    getLiveSportList(): Observable<TagCount[] | undefined> {
        const tagsToFetch = [TagType.Sport, TagType.Region, TagType.Tournament, TagType.Competition, TagType.VirtualCompetition].join(',');

        return this.cdsApi.get<TagCount[]>('/counts', {
            state: FixtureState.Live,
            tagTypes: tagsToFetch,
            extendedTags: tagsToFetch,
            sortBy: SortByCriteria.Tags,
        });
    }

    getPrematchEventTree(region: number, sport: number): Observable<TagCount[] | undefined> {
        const request: FixtureCountsRequest = {
            state: FixtureState.Latest,
            tagTypes: [TagType.Sport, TagType.Region, TagType.Tournament, TagType.Competition].join(','),
            extendedTags: [TagType.Sport, TagType.Region, TagType.Tournament, TagType.Competition].join(','),
            sortBy: SortByCriteria.Tags,
            sportIds: sport.toString(),
        };

        if (sport === SportConstant.Tennis) {
            request.tournamentIds = region.toString();
        } else {
            request.regionIds = region.toString();
        }

        return this.cdsApi.get<TagCount[]>('/counts', request);
    }

    getSport(
        sport: number,
        includeDynamicCategories?: boolean,
        interval?: {
            from?: string;
            to?: string;
        },
    ): Observable<TagCount[] | undefined> {
        return this.cdsApi.get<TagCount[]>('/counts', {
            state: FixtureState.Latest,
            tagTypes: [
                TagType.Sport,
                TagType.Region,
                TagType.Tournament,
                TagType.Competition,
                TagType.VirtualCompetition,
                TagType.VirtualCompetitionGroup,
            ].join(','),
            extendedTags: [
                TagType.Sport,
                TagType.Region,
                TagType.Tournament,
                TagType.Competition,
                TagType.VirtualCompetition,
                TagType.VirtualCompetitionGroup,
            ].join(','),
            sportIds: sport,
            sortBy: SortByCriteria.Tags,
            ...interval,
            participantMapping: ParticipantMapping.All,
            includeDynamicCategories: !!includeDynamicCategories,
        });
    }

    getCountsPerSport(sportId: SportConstant, tagType: TagType): Observable<TagCount[] | undefined> {
        return this.cdsApi.get<TagCount[]>('/counts', {
            state: FixtureState.Latest,
            tagTypes: tagType,
            sportIds: sportId,
        });
    }

    getCountsForConference(
        conferenceId?: number,
        competitionId?: number,
        interval?: { from?: string; to?: string },
        includeDynamicCategories?: boolean,
    ): Observable<TagCount[] | undefined> {
        return this.cdsApi.get<TagCount[]>('/counts', {
            tagTypes: TagType.Conference.toString(),
            extendedTags: '',
            conferenceIds: conferenceId?.toString(),
            competitionIds: competitionId?.toString(),
            includeDynamicCategories: !!includeDynamicCategories,
            ...interval,
        });
    }

    getFixtureList(request: FixtureRequest): Observable<FixturePage | undefined> {
        return this.cdsApi.get<FixturePage>(`/fixtures`, request);
    }

    getCountsFixtures(request: CountsFixturesRequest): Observable<CountsFixturesResponse | undefined> {
        return this.cdsApi.post<CountsFixturesResponse>(`/counts-fixtures`, request);
    }

    getBatchFixtureList(request: BatchRequest<FixtureRequest>[]): Observable<BatchResponse | undefined> {
        const pagedRequests = chunk(request, this.cdsConfig.maxFixtureBatchSize);
        const requestObservables = pagedRequests.map((r: any) => this.cdsApi.post<BatchResponse>(`/fixtures-batch`, r));

        return forkJoin(requestObservables).pipe(
            catchError((err) => {
                this.logger.error(err, `Failed calling fixture-batch endpoint. Request: ${JSON.stringify(request)}`);

                return [];
            }),
            map((results: (BatchResponse | undefined)[]) => merge({}, ...results.filter(isDefined)) as BatchResponse),
        );
    }

    getFixtureOffers(request: FixtureRequest): Observable<FixtureOfferItems | undefined> {
        return this.cdsApi.get<FixtureOfferItems>(`/fixture-offers`, request);
    }

    getFixtureListBatch(request: BatchRequest<FixtureRequest>[]): Observable<BatchResponse | undefined> {
        const pagedRequests = chunk(request, this.cdsConfig.maxFixtureBatchSize);
        const requestObservables = pagedRequests.map((r: any) => this.cdsApi.post<BatchResponse>(`/fixtures-merged-batches`, r));

        return forkJoin(requestObservables).pipe(
            catchError((err) => {
                this.logger.error(err, `Failed calling fixtures-merged-batches endpoint. Request: ${JSON.stringify(request)}`);

                return [];
            }),
            map((results: (BatchResponse | undefined)[]) => merge({}, ...results.filter(isDefined)) as BatchResponse),
        );
    }

    getBatchCounts(request: BatchRequest<FixtureCountsRequest>[]): Observable<
        | {
              [key: string]: TagCount[];
          }
        | undefined
    > {
        const pagedRequests = chunk(request, this.cdsConfig.maxFixtureBatchSize);
        const requestObservables = pagedRequests.map((r: any) =>
            this.cdsApi.post<{
                [key: string]: TagCount[];
            }>('/counts-batch', r),
        );

        return forkJoin(requestObservables).pipe(
            catchError((err) => {
                this.logger.error(err, `Failed calling counts-batch endpoint. Request: ${JSON.stringify(request)}`);

                return [];
            }),
            map((results) => merge({}, ...results.filter(isDefined)) as { [key: string]: TagCount[] }),
        );
    }

    getLiveHighlights(request: LiveHighlightsRequest): Observable<LiveHighlightsResponse | undefined> {
        return this.cdsApi.get<LiveHighlightsResponse>('/live/highlights', request);
    }

    getLiveEsports(request: LiveHighlightsRequest): Observable<LiveHighlightsResponse | undefined> {
        return this.cdsApi.get<LiveHighlightsResponse>('/live/esports', request);
    }

    getLiveStreams(request: LiveStreamingRequest): Observable<LiveHighlightsResponse | undefined> {
        return this.cdsApi.get<LiveHighlightsResponse>('/live/streams', request);
    }

    private useBaseTranslation(): boolean {
        return this.baseTranslationConfig.isEnabled && this.baseTranslationConfig.defaultLanguage.toUpperCase() !== this.page.lang.toUpperCase();
    }

    private buildPicksRequestKey(prefix: string, request: PicksRequest): string {
        return concat(
            request.tv1Picks.map((p) => this.buildPickV1Key(prefix, p)),
            request.tv2Picks.map((p) => this.buildPickV2Key(prefix, p)),
        ).join('|');
    }

    private buildPickV1Key(prefix: string, pick: Tv1PickData): string {
        return `${prefix}-${pick.fixtureId}-${pick.gameId}-${pick.resultId}-${pick.useLiveFallback || false}`;
    }

    private buildPickV2Key(prefix: string, pick: Tv2PickData): string {
        return `${prefix}-${pick.fixtureId}-${pick.optionMarketId}-${pick.participantId}-${pick.isClassicBetBuilder}`;
    }
}
